// @ts-check

import { AlertCircle, CheckCircle, ChevronRight, Clock, RefreshCw, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import Modal from '../components/ui/Modal';
import { useToast } from '../context/ToastContext';
import api from '../utils/apiClient';

const WILL_SYNC = [
  { label: 'Products', description: 'All product records, metadata, and attributes' },
  { label: 'Product Images', description: 'All image references and Cloudinary URLs' },
  { label: 'Product Variants', description: 'Sizes, colors, and SKU-level data' },
  { label: 'Collections', description: 'All collection groups and membership rules' },
  { label: 'Site Settings', description: 'Hero slides, banners, and global config' },
  { label: 'Hero Slides', description: 'Homepage carousel content' },
];

const WONT_SYNC = [
  { label: 'Customers', description: 'Customer accounts and personal data' },
  { label: 'Orders', description: 'Purchase history and order records' },
  { label: 'Payments', description: 'Razorpay transaction data' },
  { label: 'Analytics', description: 'Event logs and reporting data' },
];

const STATUS_ICONS = {
  done: <CheckCircle size={16} className="text-green-600 dark:text-green-400 flex-shrink-0" />,
  running: <RefreshCw size={16} className="text-blue-600 dark:text-blue-400 flex-shrink-0 animate-spin" />,
  pending: <Clock size={16} className="text-muted-foreground/70 flex-shrink-0" />,
  failed: <XCircle size={16} className="text-red-600 dark:text-red-400 flex-shrink-0" />,
};

const StagingSyncPage = () => {
  const { showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [syncState, setSyncState] = useState('idle'); // idle | running | completed | failed
  const [taskId, setTaskId] = useState(null);
  const [progress, setProgress] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);

  const pollingRef = useRef(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const startPolling = (id) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    const poll = async () => {
      try {
        const data = await api.get(`/api/admin/sync-to-staging/status/${id}`);
        setProgress(data.progress || []);

        if (data.status === 'completed') {
          setSyncState('completed');
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          showToast('Sync completed successfully', 'success');
        } else if (data.status === 'failed') {
          setSyncState('failed');
          setErrorMessage(data.error || 'Sync failed — check backend logs for details.');
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          showToast('Sync failed', 'error');
        }
      } catch (err) {
        setSyncState('failed');
        setErrorMessage(err.message || 'Failed to fetch sync status.');
        clearInterval(pollingRef.current);
        pollingRef.current = null;
        showToast('Lost connection to sync task', 'error');
      }
    };

    poll(); // immediate first check
    pollingRef.current = setInterval(poll, 2000);
  };

  const handleStartSync = async () => {
    setShowConfirmModal(false);
    setSyncState('running');
    setProgress([]);
    setErrorMessage(null);
    setTaskId(null);

    try {
      const data = await api.post('/api/admin/sync-to-staging');
      setTaskId(data.task_id);
      startPolling(data.task_id);
    } catch (err) {
      setSyncState('failed');
      setErrorMessage(err.message || 'Failed to start sync task.');
      showToast('Failed to start sync: ' + (err.message || 'Unknown error'), 'error');
    }
  };

  const handleReset = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setSyncState('idle');
    setTaskId(null);
    setProgress([]);
    setErrorMessage(null);
  };

  const isSyncing = syncState === 'running';
  const syncDone = syncState === 'completed';
  const syncFailed = syncState === 'failed';

  const tablesCompleted = progress.filter((p) => p.status === 'done').length;
  const rowsCompleted = progress.filter((p) => p.status === 'done').reduce((sum, p) => sum + (p.rows || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-serif">Sync Production → Staging</h1>
          <p className="text-muted-foreground mt-1">Push catalog data from production to the staging environment</p>
        </div>

        {(syncDone || syncFailed) && (
          <button
            onClick={handleReset}
            className="flex items-center space-x-2 px-4 py-2 border border-input rounded-lg hover:bg-accent text-sm"
          >
            <RefreshCw size={16} />
            <span>Reset</span>
          </button>
        )}
      </div>

      {/* Warning banner */}
      <div className="bg-amber-50 dark:bg-amber-950/50 border border-amber-300 dark:border-amber-800 rounded-lg p-4 flex items-start space-x-3">
        <AlertCircle className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" size={20} />
        <div>
          <p className="font-medium text-amber-900 dark:text-amber-300">
            Destructive operation — staging data will be overwritten
          </p>
          <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
            This will overwrite all product, collection, and content data on the staging database. Customer and order
            data is <strong>NOT</strong> affected.
          </p>
        </div>
      </div>

      {/* What will / won't be synced */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Will sync */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-green-50 dark:bg-green-950/50">
            <h2 className="text-sm font-semibold text-green-800 dark:text-green-300 flex items-center space-x-2">
              <CheckCircle size={15} />
              <span>Will be synced</span>
            </h2>
          </div>
          <ul className="divide-y divide-border/60">
            {WILL_SYNC.map((item) => (
              <li key={item.label} className="px-5 py-3 flex items-start space-x-3">
                <ChevronRight size={14} className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Won't sync */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-muted/50">
            <h2 className="text-sm font-semibold text-foreground/80 flex items-center space-x-2">
              <XCircle size={15} />
              <span>Will NOT be synced</span>
            </h2>
          </div>
          <ul className="divide-y divide-border/60">
            {WONT_SYNC.map((item) => (
              <li key={item.label} className="px-5 py-3 flex items-start space-x-3">
                <XCircle size={14} className="text-muted-foreground/70 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground/80">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.description}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Start sync button — only visible when idle */}
      {syncState === 'idle' && (
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowConfirmModal(true)}
            className="flex items-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
          >
            <RefreshCw size={18} />
            <span>Start Sync</span>
          </button>
          <p className="text-sm text-muted-foreground">A confirmation step is required before the sync begins.</p>
        </div>
      )}

      {/* Live progress log */}
      {(isSyncing || syncDone || syncFailed) && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground flex items-center space-x-2">
              {isSyncing && <RefreshCw size={15} className="animate-spin text-blue-600 dark:text-blue-400" />}
              {syncDone && <CheckCircle size={15} className="text-green-600 dark:text-green-400" />}
              {syncFailed && <XCircle size={15} className="text-red-600 dark:text-red-400" />}
              <span>
                {isSyncing && 'Sync in progress…'}
                {syncDone && 'Sync complete'}
                {syncFailed && 'Sync failed'}
              </span>
            </h2>
            {taskId && <span className="text-xs text-muted-foreground/70 font-mono">task: {taskId}</span>}
          </div>

          {/* Progress rows */}
          {progress.length > 0 ? (
            <ul className="divide-y divide-border/60">
              {progress.map((item, idx) => (
                <li key={idx} className="px-5 py-3 flex items-center space-x-3">
                  {STATUS_ICONS[item.status] || STATUS_ICONS.pending}
                  <span className="text-sm font-medium text-foreground flex-1">{item.table}</span>
                  {item.status === 'done' && (
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {item.rows?.toLocaleString()} rows
                    </span>
                  )}
                  {item.status === 'running' && (
                    <span className="text-xs text-blue-600 dark:text-blue-400 animate-pulse">syncing…</span>
                  )}
                  {item.status === 'failed' && <span className="text-xs text-red-600 dark:text-red-400">failed</span>}
                </li>
              ))}
            </ul>
          ) : (
            isSyncing && (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground flex items-center justify-center space-x-2">
                <RefreshCw size={16} className="animate-spin text-blue-500" />
                <span>Starting sync task…</span>
              </div>
            )
          )}

          {/* Summary footer */}
          {syncDone && (
            <div className="px-5 py-4 bg-green-50 dark:bg-green-950/50 border-t border-green-200 dark:border-green-900">
              <p className="text-sm font-medium text-green-900 dark:text-green-300">
                Sync complete — {tablesCompleted} table{tablesCompleted !== 1 ? 's' : ''} synced,{' '}
                {rowsCompleted.toLocaleString()} total row{rowsCompleted !== 1 ? 's' : ''} copied
              </p>
            </div>
          )}

          {/* Error footer */}
          {syncFailed && errorMessage && (
            <div className="px-5 py-4 bg-red-50 dark:bg-red-950/50 border-t border-red-200 dark:border-red-900">
              <p className="text-sm font-medium text-red-900 dark:text-red-300">Error: {errorMessage}</p>
            </div>
          )}
        </div>
      )}

      {/* Confirmation modal */}
      {showConfirmModal && (
        <Modal maxWidth="md" closeOnBackdrop={false} className="rounded-lg shadow-xl flex flex-col">
          {/* Modal header */}
          <div className="flex items-center space-x-3 px-6 py-4 border-b border-border">
            <AlertCircle className="text-red-600 dark:text-red-400 flex-shrink-0" size={22} />
            <h3 className="text-lg font-medium text-foreground">Confirm Staging Sync</h3>
          </div>

          {/* Modal body */}
          <div className="p-6 space-y-4 overflow-y-auto">
            <p className="text-foreground/80">
              This will <strong>replace all staging catalog data</strong> with a copy of the current production catalog.
            </p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li className="flex items-center space-x-2">
                <CheckCircle size={13} className="text-green-600 dark:text-green-400" />
                <span>Products, images, variants, collections, and site settings will be overwritten</span>
              </li>
              <li className="flex items-center space-x-2">
                <XCircle size={13} className="text-muted-foreground/70" />
                <span>Customer, order, and payment data will NOT be touched</span>
              </li>
            </ul>
            <div className="bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 rounded-lg p-3 text-sm text-red-800 dark:text-red-300">
              Any changes made directly on staging will be lost. This action cannot be undone.
            </div>
          </div>

          {/* Modal footer */}
          <div className="px-6 py-4 bg-muted/50 border-t border-border flex justify-end space-x-3 flex-shrink-0">
            <button
              onClick={() => setShowConfirmModal(false)}
              className="px-4 py-2 border border-input rounded-lg hover:bg-accent text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleStartSync}
              className="flex items-center space-x-2 px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium transition-colors"
            >
              <RefreshCw size={15} />
              <span>Yes, start sync</span>
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default StagingSyncPage;
